import { AnyAction, Dispatch } from 'redux';

import { Actions } from 'src/store/spinner/actions';
import { IFilterAllData, ListOfKeysFilterAllData, PlacementShowModule } from 'src/typings/filter';
import { IFirstSpinner } from 'src/typings/spinner';

interface SpinnerControls {
  frame: { x: number };
  setRotateTo: (position: [string, number], animate: boolean) => void;
}

interface PlacementSpinner {
  controls: SpinnerControls;
  addEventListener: (event: string, listener: () => void, capture: boolean) => void;
  removeEventListener: (event: string, listener: () => void, capture: boolean) => void;
}

const isPlacementSpinner = (spinner: unknown): spinner is PlacementSpinner => {
  const candidate = spinner as Partial<PlacementSpinner> | null | undefined;
  return Boolean(
    candidate
    && typeof candidate.addEventListener === 'function'
    && typeof candidate.removeEventListener === 'function'
    && candidate.controls
    && Number.isFinite(Number(candidate.controls.frame?.x))
    && typeof candidate.controls.setRotateTo === 'function',
  );
};

export const canShowPlacementInSpinner = (
  placement: IFilterAllData | null | undefined,
  currentSpinner: IFirstSpinner | null,
) => {
  if (!placement
    || placement[ListOfKeysFilterAllData.placementStatusShowInModule] === PlacementShowModule.hide
    || !currentSpinner) return false;

  return Boolean(placement[ListOfKeysFilterAllData.placementBestSpinnerStops]?.some(
    (stop) => stop['entity-view-name'] === currentSpinner.name,
  ));
};

export const showPlacementInSpinner = ({
  placement,
  currentSpinner,
  dispatch,
  spinner = document.mainSpinner,
}: {
  placement: IFilterAllData | null | undefined;
  currentSpinner: IFirstSpinner | null;
  dispatch: Dispatch<AnyAction>;
  spinner?: unknown;
}): boolean => {
  if (!canShowPlacementInSpinner(placement, currentSpinner) || !isPlacementSpinner(spinner)) return false;

  const supportedSpinner = spinner as PlacementSpinner;
  const stop = placement![ListOfKeysFilterAllData.placementBestSpinnerStops]!.find(
    (candidate) => candidate['entity-view-name'] === currentSpinner!.name,
  )!;
  const id = String(placement![ListOfKeysFilterAllData.id]);
  const highlight = () => dispatch(Actions.setHighlightPolygons([id]));
  const needsRotation = Math.floor(supportedSpinner.controls.frame.x) !== Math.floor(Number(stop.stopNumber));

  if (needsRotation) {
    const onRotateEnd = () => {
      supportedSpinner.removeEventListener('rotate-end', onRotateEnd, false);
      highlight();
    };
    supportedSpinner.addEventListener('rotate-end', onRotateEnd, false);
    supportedSpinner.controls.setRotateTo([stop.stopNumber, 0], true);
  }

  highlight();
  return true;
};
